/**
 * SA-1329 (spec §4.4): the Triage tab queue's bulk wiring. A queue item is not
 * a decidable row (an email item stands for its waiting rows, and the list serves
 * no row ids for it), so each selected thread's detail is read at the press and
 * the run is built from those rows only.
 */
import { useMemo, useRef, useState } from 'react'
import { usePermission } from '@/hooks/usePermission'
import { startTriageRun } from '@/services/triageRun'
import { readTriageDetails } from '@/services/triageDetails'
import { triageErrorMessage } from '@/services/triage'
import { useInboxStore } from '@/stores/inboxStore'
import type { InboxConversationDetail, QueueItem, TriageKindOption } from '@/types/inbox'
import type { TriageSelectionBarProps } from './TriageSelectionBar'
import {
  bulkVerdict, candidateFromItem, candidateFromRow, changeKindChoices, confirmText, type BulkCandidate,
} from './triageBulk'
import {
  approveOf, approveReason, BULK_KIND_EXCLUDED, DISMISS_HELD, NEEDS_KIND_REASON, needsChoiceText,
  NONE_CAN_RUN, NOTHING_TO_DECIDE, RUN_BUSY, skippedText,
} from './triageText'
import { useTriageSelection } from './useTriageSelection'

type Mode = 'approve' | 'change' | 'dismiss' | 'rerun'

interface Judged {
  /** What the run would decide. */
  run: BulkCandidate[]
  /** Reasons, one per message held back. */
  skipped: string[]
  /** Each thread's kinds, for Change kind. */
  kindSets: TriageKindOption[][]
}

function approveSkip(c: BulkCandidate, noun: string): string {
  switch (c.state) {
    case 'needs_record': return needsChoiceText('record', noun)
    case 'needs_outcome': return needsChoiceText('outcome', noun)
    case 'needs_kind': return NEEDS_KIND_REASON
    case 'refused': return c.refusal ?? approveReason.refused
    default: return NOTHING_TO_DECIDE
  }
}

/** Judges the selection from the threads as they read now (pure). */
export function judgeDetails(
  selected: readonly QueueItem[],
  details: ReadonlyMap<string, InboxConversationDetail>,
  mode: Mode,
  ctx: { canTriage: boolean; noun: string },
): Judged {
  const out: Judged = { run: [], skipped: [], kindSets: [] }
  const seen = new Set<string>()
  for (const item of selected) {
    const detail = details.get(item.conversationId)
    const triage = detail?.triage
    if (!detail || !triage) { out.skipped.push(NOTHING_TO_DECIDE); continue }
    const openRecords = (detail.records ?? []).filter((r) => r.state === 'open')
    const label = item.title ?? item.senderName ?? ''
    const rows = item.ledgerId ? triage.rows.filter((r) => r.ledgerId === item.ledgerId) : triage.rows
    const cands = rows.flatMap((row) => {
      const cand = candidateFromRow(row, triage, { conversationId: detail.id, canTriage: ctx.canTriage, noun: ctx.noun, openRecords, label })
      return cand && !seen.has(cand.key) ? [cand] : []
    })
    // A failed message is still acted on (spec 3.3): re-run, change kind or dismiss it; never approve it.
    const wanted = cands.filter((c) => (mode === 'rerun' ? c.state === 'failed' : mode === 'approve' ? c.state !== 'failed' : true))
    if (!wanted.length) { out.skipped.push(NOTHING_TO_DECIDE); continue }
    out.kindSets.push(triage.kinds)
    for (const c of wanted) {
      seen.add(c.key)
      if (mode === 'rerun') {
        if (c.canRerun && c.mailboxId) out.run.push(c); else out.skipped.push(NOTHING_TO_DECIDE)
      } else if (mode === 'approve') {
        if (c.state === 'approvable') out.run.push(c); else out.skipped.push(approveSkip(c, ctx.noun))
      } else if (!c.canDecide) {
        out.skipped.push(NOTHING_TO_DECIDE)
      } else if (mode === 'dismiss' && c.dismissRefusal) {
        out.skipped.push(DISMISS_HELD)
      } else {
        out.run.push(c)
      }
    }
  }
  return out
}

function skippedLines(reasons: readonly string[]): string[] {
  const counts = new Map<string, number>()
  for (const r of reasons) counts.set(r, (counts.get(r) ?? 0) + 1)
  return [...counts].map(([reason, n]) => skippedText(n, reason))
}

interface Args {
  items: readonly QueueItem[]
  noun: string
  resetToken: string
}

export function useQueueBulk({ items, noun, resetToken }: Args) {
  const canTriage = usePermission('inbox:triage')
  const keys = useMemo(() => items.map((i) => i.id), [items])
  const sel = useTriageSelection(keys, resetToken)
  const run = useInboxStore((s) => s.triageRun)
  const [checking, setChecking] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [skippedNote, setSkippedNote] = useState<{ runId: string; lines: string[] } | null>(null)
  const [confirm, setConfirm] = useState<{ judged: Judged; total: number } | null>(null)
  const [kinds, setKinds] = useState<'loading' | { judged: Judged; choices: TriageKindOption[]; note: string | null } | null>(null)
  const seq = useRef(0)

  const picked = items.filter((i) => sel.isSelected(i.id))
  const running = run?.status === 'running'
  // A run going from any surface takes the bar (spec 9.7); a finished one only where it began.
  const barRun = run && (running || run.origin === 'queue') ? run : null
  const barOn = picked.length > 0 || barRun !== null

  // Press-time state belongs to the selection it was made for: any change of the
  // picks or of the filters drops it, and a read still in flight is outdated.
  const selectionKey = JSON.stringify([resetToken, picked.map((i) => i.id)])
  const [lastKey, setLastKey] = useState(selectionKey)
  if (lastKey !== selectionKey) {
    setLastKey(selectionKey)
    seq.current += 1
    setConfirm(null)
    setKinds(null)
    setChecking(null)
    setError(null)
  }

  const begin = () => {
    seq.current += 1
    setError(null)
    setChecking(picked.length)
    return seq.current
  }
  /** Reads the picked threads; null when the press was cancelled meanwhile or the read failed. */
  const read = async (mode: Mode): Promise<Judged | null> => {
    const mine = begin()
    try {
      const details = await readTriageDetails(picked.map((i) => i.conversationId))
      if (mine !== seq.current) return null
      setChecking(null)
      return judgeDetails(picked, details, mode, { canTriage, noun })
    } catch (err) {
      if (mine !== seq.current) return null
      setChecking(null)
      setError(triageErrorMessage(err))
      return null
    }
  }

  const start = (action: Mode, cands: readonly BulkCandidate[], kind?: string, skipped: readonly string[] = []) => {
    const id = startTriageRun({
      action,
      origin: 'queue',
      kind,
      items: cands.map((c) => ({
        key: c.key,
        conversationId: c.conversationId,
        ledgerId: c.ledgerId as string,
        mailboxId: c.mailboxId,
        label: c.label,
        ...(action === 'approve' && c.outcome ? { outcome: c.outcome } : {}),
      })),
    })
    setConfirm(null)
    setKinds(null)
    if (!id) {
      // Another run holds the store: say so rather than drop the press.
      setError(RUN_BUSY)
      return
    }
    if (skipped.length) setSkippedNote({ runId: id, lines: skippedLines(skipped) })
    sel.clear()
  }

  const onApprove = async () => {
    const judged = await read('approve')
    if (judged) setConfirm({ judged, total: judged.run.length + judged.skipped.length })
  }
  const direct = (mode: 'dismiss' | 'rerun') => async () => {
    const judged = await read(mode)
    if (!judged) return
    if (judged.run.length) start(mode, judged.run, undefined, judged.skipped)
    else setError(NONE_CAN_RUN)
  }
  const onOpenChangeKind = async () => {
    setKinds('loading')
    const judged = await read('change')
    if (!judged) { setKinds(null); return }
    const { choices, excluded } = changeKindChoices(judged.kindSets)
    setKinds({ judged, choices, note: excluded ? BULK_KIND_EXCLUDED(noun) : null })
  }

  const done = barRun?.status === 'done'
  const cands = picked.map(candidateFromItem)
  const text = confirm ? confirmText(confirm.judged.run) : null
  const barProps: TriageSelectionBarProps = {
    selected: barRun ? barRun.items.length : picked.length,
    total: Math.max(items.length, barRun ? barRun.items.length : 0),
    allState: sel.allState,
    onToggleAll: () => sel.setAll(sel.allState !== true),
    verdict: bulkVerdict(cands, noun),
    noun,
    onApprove: () => { void onApprove() },
    onRerun: () => { void direct('rerun')() },
    onDismiss: () => { void direct('dismiss')() },
    changeKind: kinds === 'loading' ? 'loading' : kinds ? { choices: kinds.choices, note: kinds.note } : null,
    onOpenChangeKind: () => { void onOpenChangeKind() },
    onApplyKind: (kindId) => { if (kinds && kinds !== 'loading') start('change', kinds.judged.run, kindId, kinds.judged.skipped) },
    confirm: confirm && text
      ? {
        heading: text.heading,
        lines: text.lines,
        skipped: skippedLines(confirm.judged.skipped),
        action: approveOf(confirm.judged.run.length, confirm.total),
        actionDisabled: confirm.judged.run.length === 0,
      }
      : null,
    onConfirm: () => { if (confirm) start('approve', confirm.judged.run) },
    onCancelConfirm: () => setConfirm(null),
    checking,
    run: barRun,
    error,
    notes: skippedNote && barRun?.id === skippedNote.runId ? skippedNote.lines : undefined,
    onClear: () => {
      seq.current += 1
      setSkippedNote(null)
      setConfirm(null)
      setKinds(null)
      setChecking(null)
      setError(null)
      if (done) useInboxStore.getState().clearTriageRun()
      else sel.clear()
    },
  }

  return { sel, running, barOn, barProps }
}
